import { forwardRef, useImperativeHandle, useEffect } from "react";
import { Box, Space, Text, Title } from "@mantine/core";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { Product, Order } from "../../../types/interfaces";
import { UPDATE_ORDER } from "../../../gql/orders";
import { UPDATE_REMOVABLE_ITEM } from "../../../gql/items";
import {
  Rdv2ChoiceType,
  Rdv2ReturnMethod,
  RemovableItemOrderType,
  ImprintType,
  ItemType,
} from "../../../types/enums";
import { ButtonsSelect } from "../../button/ButtonsSelect";

type Rdv2ChoiceFormData = {
  rdv2Choice: Rdv2ChoiceType | null;
  returnMethod: Rdv2ReturnMethod | null;
};

type Rdv2ChoiceProps = {
  product?: Product;
  previousProduct?: Product;
  isEditMode?: boolean;
  order?: Order;
};

export interface Rdv2ChoiceRef {
  saveData: () => Promise<void>;
}

const rdv2ChoiceOptionsAfterWaxBite = [
  {
    value: Rdv2ChoiceType.CONTINUE_TO_FINISH,
    label: "Poursuivre vers la finition",
  },
  {
    value: Rdv2ChoiceType.REQUEST_TRY_IN,
    label: "Demander un essayage",
  },
  {
    value: Rdv2ChoiceType.REQUEST_NEW_WAX_BITE,
    label: "Demander une nouvelle cire d'occlusion",
  },
];

const rdv2ChoiceOptionsAfterTryin = [
  {
    value: Rdv2ChoiceType.CONTINUE_TO_FINISH,
    label: "Poursuivre vers la finition",
  },
  {
    value: Rdv2ChoiceType.REQUEST_TRY_IN,
    label: "Demander un nouvel essayage",
  },
];

const returnMethodOptionsWaxBite = [
  {
    value: Rdv2ReturnMethod.PICKUP,
    label:
      "Je demande un ramassage de mes cires d'occlusion par transporteur et les retourne à Jasper",
  },
  {
    value: Rdv2ReturnMethod.DIGITAL_IMPRINT,
    label:
      "Je prends une empreinte numérique de mes cires d'occlusion en bouche et ne les renvoie pas à Jasper",
  },
];

export const Rdv2Choice = forwardRef<Rdv2ChoiceRef, Rdv2ChoiceProps>(
  ({ product, previousProduct, isEditMode = false, order }, ref) => {
    const previousRemovableItem = previousProduct?.removableItem?.[0];

    // Determine if we're coming from TRY_IN or WAX_BITE
    const previousOrderType = previousRemovableItem?.fullDentureOrderType;
    const isAfterTryIn = previousOrderType === RemovableItemOrderType.TRY_IN;

    // Find existing RDV2 product in order (WAX_BITE_RDV2 or TRY_IN_RDV2)
    // In isEditMode (Labs), always use the current product's removableItem
    const existingRdv2Product = isEditMode
      ? undefined
      : order?.products?.find(
          (p: Product) =>
            p.productType === ItemType.FULL_DENTURE &&
            (p.removableItem?.[0]?.imprintType === ImprintType.WAX_BITE_RDV2 ||
              p.removableItem?.[0]?.imprintType === ImprintType.TRY_IN_RDV2)
        );

    const newRemovableItem =
      existingRdv2Product?.removableItem?.[0] ?? product?.removableItem?.[0];

    const { control, watch, setValue } = useForm<Rdv2ChoiceFormData>({
      defaultValues: {
        rdv2Choice: (newRemovableItem?.rdv2Choice as Rdv2ChoiceType) ?? null,
        returnMethod:
          (newRemovableItem?.returnMethod as Rdv2ReturnMethod) ?? null,
      },
    });

    const [updateOrder] = useMutation(UPDATE_ORDER, {
      refetchQueries: ["getOrderByUniqueAttribute"],
      awaitRefetchQueries: true,
    });

    const [updateRemovableItem] = useMutation(UPDATE_REMOVABLE_ITEM, {
      refetchQueries: ["getOrderByUniqueAttribute"],
      awaitRefetchQueries: true,
    });

    const rdv2Choice = watch("rdv2Choice");
    const returnMethod = watch("returnMethod");

    const rdv2ChoiceOptions = isAfterTryIn
      ? rdv2ChoiceOptionsAfterTryin
      : rdv2ChoiceOptionsAfterWaxBite;

    const getFullDentureOrderType = (
      choice: Rdv2ChoiceType | null
    ): RemovableItemOrderType | null => {
      switch (choice) {
        case Rdv2ChoiceType.CONTINUE_TO_FINISH:
          return RemovableItemOrderType.FINAL;
        case Rdv2ChoiceType.REQUEST_TRY_IN:
          return RemovableItemOrderType.TRY_IN;
        case Rdv2ChoiceType.REQUEST_NEW_WAX_BITE:
          return RemovableItemOrderType.WAX_BITE;
        default:
          return null;
      }
    };

    // we reset returnMethod when we change to a new wax bite
    useEffect(() => {
      if (rdv2Choice === Rdv2ChoiceType.REQUEST_NEW_WAX_BITE) {
        setValue("returnMethod", null);
      }
    }, [rdv2Choice, setValue]);

    const saveData = async (): Promise<void> => {
      if (!rdv2Choice) {
        return;
      }

      const fullDentureOrderType = getFullDentureOrderType(rdv2Choice);
      const imprintType = isAfterTryIn
        ? ImprintType.TRY_IN_RDV2
        : ImprintType.WAX_BITE_RDV2;

      try {
        let finalReturnMethod: Rdv2ReturnMethod | null = null;
        if (isAfterTryIn) {
          finalReturnMethod =
            rdv2Choice === Rdv2ChoiceType.CONTINUE_TO_FINISH
              ? Rdv2ReturnMethod.PICKUP
              : null;
        } else {
          finalReturnMethod =
            rdv2Choice !== Rdv2ChoiceType.REQUEST_NEW_WAX_BITE
              ? returnMethod
              : null;
        }
        if (isEditMode) {
          if (newRemovableItem) {
            // Product already has a removableItem — UPDATE with all fields
            await updateRemovableItem({
              variables: {
                where: { id: newRemovableItem.id },
                data: {
                  fullDentureOrderType: { set: fullDentureOrderType },
                  rdv2Choice: { set: rdv2Choice },
                  returnMethod: { set: finalReturnMethod },
                  imprintType: { set: imprintType },
                  rdvNumber: {
                    set: (previousRemovableItem?.rdvNumber ?? 1) + 1,
                  },
                  ...(isAfterTryIn && previousRemovableItem?.itemMaterial?.id
                    ? {
                        itemMaterial: {
                          connect: {
                            id: previousRemovableItem.itemMaterial.id,
                          },
                        },
                      }
                    : {}),
                  ...(isAfterTryIn && previousRemovableItem?.teethShade?.id
                    ? {
                        teethShade: {
                          connect: { id: previousRemovableItem.teethShade.id },
                        },
                      }
                    : {}),
                  ...(isAfterTryIn && previousRemovableItem?.gingivaShade?.id
                    ? {
                        gingivaShade: {
                          connect: {
                            id: previousRemovableItem.gingivaShade.id,
                          },
                        },
                      }
                    : {}),
                  ...(isAfterTryIn
                    ? {
                        isBimaxillarySeparated: {
                          set:
                            previousRemovableItem?.isBimaxillarySeparated ??
                            false,
                        },
                      }
                    : {}),
                },
              },
            });
            // Copy item preferences if after TRY_IN
            if (isAfterTryIn && previousProduct?.itemPreferences) {
              await updateOrder({
                variables: {
                  where: { id: product?.orderId },
                  data: {
                    products: {
                      update: {
                        where: { id: product?.id },
                        data: {
                          itemPreferences: previousProduct.itemPreferences,
                        },
                      },
                    },
                  },
                },
              });
            }
          } else {
            // Product has no removableItem (created empty by Labs) — CREATE one
            const teeth = previousRemovableItem?.teeth ?? product?.teeth ?? [];
            const createData: any = {
              itemType: ItemType.FULL_DENTURE,
              teeth: teeth,
              fullDentureOrderType: fullDentureOrderType,
              imprintType: imprintType,
              rdvNumber: (previousRemovableItem?.rdvNumber ?? 1) + 1,
              rdv2Choice: rdv2Choice,
              returnMethod: finalReturnMethod,
              ...(previousRemovableItem?.itemMaterial?.id
                ? {
                    itemMaterial: {
                      connect: { id: previousRemovableItem.itemMaterial.id },
                    },
                  }
                : { itemMaterial: { connect: { id: "ACRYLIC" } } }),
              ...(isAfterTryIn && previousRemovableItem?.teethShade?.id
                ? {
                    teethShade: {
                      connect: { id: previousRemovableItem.teethShade.id },
                    },
                  }
                : {}),
              ...(isAfterTryIn && previousRemovableItem?.gingivaShade?.id
                ? {
                    gingivaShade: {
                      connect: { id: previousRemovableItem.gingivaShade.id },
                    },
                  }
                : {}),
              isBimaxillarySeparated:
                previousRemovableItem?.isBimaxillarySeparated ?? false,
            };

            await updateOrder({
              variables: {
                where: { id: product?.orderId },
                data: {
                  products: {
                    update: {
                      where: { id: product?.id },
                      data: {
                        ...(isAfterTryIn && previousProduct?.itemPreferences
                          ? { itemPreferences: previousProduct.itemPreferences }
                          : {}),
                        removableItem: {
                          create: [createData],
                        },
                      },
                    },
                  },
                },
              },
            });
          }
        } else if (
          existingRdv2Product &&
          newRemovableItem &&
          (previousRemovableItem?.rdvNumber ?? 1) === 1
        ) {
          // RDV2 product already exists - UPDATE instead of CREATE
          await updateRemovableItem({
            variables: {
              where: { id: newRemovableItem.id },
              data: {
                fullDentureOrderType: { set: fullDentureOrderType },
                rdv2Choice: { set: rdv2Choice },
                returnMethod: { set: finalReturnMethod },
              },
            },
          });
        } else if (isAfterTryIn && previousRemovableItem) {
          // After TRY_IN: Copy characteristics from previous product
          const createData: any = {
            itemType: ItemType.FULL_DENTURE,
            teeth: previousRemovableItem.teeth ?? [],
            fullDentureOrderType: fullDentureOrderType,
            imprintType: imprintType,
            rdvNumber: (previousRemovableItem.rdvNumber ?? 1) + 1,
            rdv2Choice: rdv2Choice,
            returnMethod:
              rdv2Choice === Rdv2ChoiceType.CONTINUE_TO_FINISH
                ? Rdv2ReturnMethod.PICKUP
                : null,
            // Copy material from previous
            ...(previousRemovableItem.itemMaterial?.id
              ? {
                  itemMaterial: {
                    connect: { id: previousRemovableItem.itemMaterial.id },
                  },
                }
              : {}),
            // Copy teeth shade from previous
            ...(previousRemovableItem.teethShade?.id
              ? {
                  teethShade: {
                    connect: { id: previousRemovableItem.teethShade.id },
                  },
                }
              : {}),
            // Copy gingiva shade from previous
            ...(previousRemovableItem.gingivaShade?.id
              ? {
                  gingivaShade: {
                    connect: { id: previousRemovableItem.gingivaShade.id },
                  },
                }
              : {}),
            // Copy bimaxillary separation setting
            isBimaxillarySeparated:
              previousRemovableItem.isBimaxillarySeparated ?? false,
          };

          await updateOrder({
            variables: {
              where: { id: product?.orderId },
              data: {
                products: {
                  create: [
                    {
                      productType: ItemType.FULL_DENTURE,
                      teeth: previousRemovableItem.teeth ?? [],
                      // Copy item preferences from previous product
                      ...(previousProduct?.itemPreferences
                        ? { itemPreferences: previousProduct.itemPreferences }
                        : {}),
                      removableItem: {
                        create: [createData],
                      },
                    },
                  ],
                },
              },
            },
          });
        } else if (newRemovableItem) {
          // After WAX_BITE: Create new product without copying characteristics
          // (they will be set in Material and Aesthetics steps)
          await updateOrder({
            variables: {
              where: { id: product?.orderId },
              data: {
                products: {
                  create: [
                    {
                      productType: ItemType.FULL_DENTURE,
                      teeth: newRemovableItem.teeth,
                      removableItem: {
                        create: [
                          {
                            itemType: ItemType.FULL_DENTURE,
                            teeth: newRemovableItem.teeth,
                            fullDentureOrderType: fullDentureOrderType,
                            imprintType: imprintType,
                            rdvNumber: 2,
                            rdv2Choice: rdv2Choice,
                            returnMethod:
                              rdv2Choice !== Rdv2ChoiceType.REQUEST_NEW_WAX_BITE
                                ? returnMethod
                                : null,
                          },
                        ],
                      },
                    },
                  ],
                },
              },
            },
          });
        }
      } catch (error) {
        console.error("Error in saveData:", error);
      }
    };

    useImperativeHandle(ref, () => ({ saveData }));

    if (!previousRemovableItem && !newRemovableItem) {
      return (
        <div>
          <Text>
            {"Erreur sur l'ajout de produit. Veuillez contacter le support."}
          </Text>
        </div>
      );
    }

    const showReturnMethodOptions =
      !isAfterTryIn &&
      (rdv2Choice === Rdv2ChoiceType.CONTINUE_TO_FINISH ||
        rdv2Choice === Rdv2ChoiceType.REQUEST_TRY_IN);

    return (
      <Box>
        <Title order={3}>
          Comment souhaitez-vous procéder pour cette commande ?
        </Title>
        <Space h="md" />
        <Controller
          name="rdv2Choice"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <ButtonsSelect
              data={rdv2ChoiceOptions}
              value={field.value}
              onChange={value => field.onChange(value)}
              numberOfColumns={1}
              align="left"
            />
          )}
        />
        {showReturnMethodOptions && (
          <>
            <Space h="xl" />
            <Title order={4}>
              Comment souhaitez-vous retourner vos cires d'occlusion ?
            </Title>
            <Space h="md" />
            <Controller
              name="returnMethod"
              control={control}
              rules={{ required: showReturnMethodOptions }}
              render={({ field }) => (
                <ButtonsSelect
                  data={returnMethodOptionsWaxBite}
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  numberOfColumns={1}
                  align="left"
                />
              )}
            />
          </>
        )}
        <Space h="xl" />
      </Box>
    );
  }
);

Rdv2Choice.displayName = "Rdv2Choice";
